import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Card } from '@actual-app/components/card';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';
import { useQuery } from '@tanstack/react-query';

import { Page } from '#components/Page';
import { useAccounts } from '#hooks/useAccounts';
import { useCategories } from '#hooks/useCategories';
import type { PlanningGoal } from '#planning/types';
import { usePlanningGoals } from '#planning/usePlanningGoals';
import { getPlanningBudgetCategoryMap } from '#planning/utils';

import { PlanningGoalCard } from './PlanningGoalCard';
import { PlanningGoalWizard } from './PlanningGoalWizard';

export function PlanningPage() {
  const { t } = useTranslation();
  const currentMonth = monthUtils.currentMonth();
  const { data: accounts = [] } = useAccounts();
  const { data: categories = { grouped: [], list: [] } } = useCategories();
  const { goals, deleteGoal, setGoals, upsertGoal } = usePlanningGoals();
  const [isWizardOpen, setWizardOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<PlanningGoal | null>(null);

  const { data: budgetMonth } = useQuery({
    queryKey: ['planning', 'budget-month', currentMonth],
    queryFn: () => send('api/budget-month', { month: currentMonth }),
    staleTime: 60_000,
  });

  const accountsById = useMemo(
    () => new Map(accounts.map(account => [account.id, account])),
    [accounts],
  );
  const categoriesById = useMemo(
    () => new Map(categories.list.map(category => [category.id, category])),
    [categories.list],
  );
  const budgetCategoriesById = useMemo(
    () => getPlanningBudgetCategoryMap(budgetMonth),
    [budgetMonth],
  );

  useEffect(() => {
    if (budgetCategoriesById.size === 0) {
      return;
    }

    let hasChanges = false;
    const migratedGoals = goals.map(goal => {
      if (goal.categoryBalanceAtLastSync != null || !goal.categoryId) {
        return goal;
      }

      const categorySummary = budgetCategoriesById.get(goal.categoryId);
      if (!categorySummary) {
        return goal;
      }

      hasChanges = true;
      return {
        ...goal,
        categoryBalanceAtLastSync: categorySummary.balance,
      };
    });

    if (hasChanges) {
      setGoals(migratedGoals);
    }
  }, [budgetCategoriesById, goals, setGoals]);
  const sortedGoals = useMemo(
    () =>
      [...goals].sort((firstGoal, secondGoal) => {
        if (firstGoal.targetMonth && !secondGoal.targetMonth) {
          return -1;
        }
        if (!firstGoal.targetMonth && secondGoal.targetMonth) {
          return 1;
        }
        if (firstGoal.targetMonth && secondGoal.targetMonth) {
          return firstGoal.targetMonth.localeCompare(secondGoal.targetMonth);
        }
        return firstGoal.name.localeCompare(secondGoal.name);
      }),
    [goals],
  );

  return (
    <Page header={t('Planning')}>
      <View style={{ paddingTop: 18, paddingBottom: 40 }}>
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <View>
            <Text
              style={{
                display: 'block',
                fontSize: 28,
                lineHeight: '1.1em',
                fontWeight: 700,
              }}
            >
              {t('Active goals')}
            </Text>
            <Text
              style={{
                display: 'block',
                marginTop: 6,
                color: theme.pageTextSubdued,
              }}
            >
              {t(
                'Tie reserves, sinking funds, and payoff targets to real accounts and real categories.',
              )}
            </Text>
          </View>

          <Button
            variant="primary"
            onPress={() => {
              setEditingGoal(null);
              setWizardOpen(true);
            }}
          >
            {t('Add goal')}
          </Button>
        </View>

        {sortedGoals.length === 0 ? (
          <Card
            style={{
              marginLeft: 0,
              marginRight: 0,
              marginTop: 18,
            }}
          >
            <View style={{ padding: 24 }}>
              <Text
                style={{
                  display: 'block',
                  fontSize: 24,
                  lineHeight: '1.15em',
                  fontWeight: 700,
                }}
              >
                {t('Start with the next goal that needs a real plan.')}
              </Text>
              <Text
                style={{
                  display: 'block',
                  marginTop: 8,
                  maxWidth: 620,
                  color: theme.pageTextSubdued,
                }}
              >
                {t(
                  'Pick the category that should carry the monthly funding, add the accounts that already hold some of the money, and give yourself a target pace.',
                )}
              </Text>
              <View style={{ marginTop: 18 }}>
                <Button
                  variant="primary"
                  onPress={() => {
                    setEditingGoal(null);
                    setWizardOpen(true);
                  }}
                >
                  {t('Create your first goal')}
                </Button>
              </View>
            </View>
          </Card>
        ) : (
          <View
            style={{
              marginTop: 18,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: 16,
            }}
          >
            {sortedGoals.map(goal => (
              <PlanningGoalCard
                key={goal.id}
                goal={goal}
                accountsById={accountsById}
                categoriesById={categoriesById}
                budgetCategoriesById={budgetCategoriesById}
                currentMonth={currentMonth}
                onEdit={selectedGoal => {
                  setEditingGoal(selectedGoal);
                  setWizardOpen(true);
                }}
                onDelete={selectedGoal => {
                  if (
                    window.confirm(
                      t('Delete "{{goalName}}"?', {
                        goalName: selectedGoal.name,
                      }),
                    )
                  ) {
                    deleteGoal(selectedGoal.id);
                  }
                }}
              />
            ))}
          </View>
        )}
      </View>

      <PlanningGoalWizard
        open={isWizardOpen}
        goal={editingGoal}
        accounts={accounts}
        categoryGroups={categories.grouped}
        budgetCategoriesById={budgetCategoriesById}
        currentMonth={currentMonth}
        onClose={() => {
          setWizardOpen(false);
          setEditingGoal(null);
        }}
        onSave={goal => {
          upsertGoal(goal);
        }}
      />
    </Page>
  );
}
